'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * How wide the centre's own body is, so it can put the preferences beside the
 * days or behind a button.
 *
 * ═══ WHAT THIS REPLACED, AND WHY IT IS NOT THE SAME THING ═══
 *
 * The source slice read the BMS shell's `SectionWidthProvider`, which measures
 * the section COLUMN: the book puts an agent pane beside the section and
 * dragging its seam re-lays the centre with no change to the viewport, so the
 * column's width is the honest question there and a viewport media query would
 * have been wrong. That provider is the book's, it is a store, a context, a
 * host and four hooks, and there is nothing on the platform that mounts it.
 *
 * So the centre measures ITSELF. It is a cover over the whole page here rather
 * than over one column, so its own box is the column, and a `ResizeObserver` on
 * the element that holds the days answers the same question the provider did
 * without a provider to mount. It also keeps the one property that made the
 * provider worth reading: a pane, a sidebar or a zoom that changes the centre's
 * width without changing the viewport re-lays it.
 *
 * NOT A MEDIA QUERY, for that reason, and not `window.innerWidth`.
 *
 * ═══ THE FIRST FRAME IS THE VIEWPORT ═══
 *
 * Before the observer has fired, and in any environment that measures every box
 * at zero, the width is the VIEWPORT's. That is not a guess dressed as a
 * measurement: this cover is a fixed overlay at `inset-0`, so its box IS the
 * viewport until something inside it says otherwise, and a measured zero means
 * "not laid out yet" rather than "300 pixels wide". A default of null read as
 * narrow would paint one frame of the button on a wide window and would put the
 * rail permanently out of reach anywhere layout is not run at all.
 */
export interface CentreWidth {
  /** The element to measure. Put it on the box that holds the days. */
  readonly ref: (node: HTMLElement | null) => void;
  /**
   * The width in CSS pixels, or null before the element has been handed over.
   * A box that measures zero answers with the viewport instead; see above.
   */
  readonly width: number | null;
}

/** The viewport, where there is one. Zero on the server, which reads as narrow. */
const viewportWidth = (): number => typeof window === 'undefined' ? 0 : window.innerWidth;
export const useCentreWidth = () => {
  const $ = _c(5);
  const [width, setWidth] = useState(null);
  const observer = useRef(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => () => {
      observer.current?.disconnect();
      observer.current = null;
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = node => {
      observer.current?.disconnect();
      observer.current = null;
      if (node === null) {
        return;
      }
      const measure = value => {
        setWidth(value > 0 ? value : viewportWidth());
      };
      if (typeof ResizeObserver === "undefined") {
        measure(node.getBoundingClientRect().width);
        return;
      }
      const next = new ResizeObserver(entries => {
        const entry = entries[0];
        if (entry !== undefined) {
          measure(entry.contentRect.width);
        }
      });
      next.observe(node);
      observer.current = next;
      measure(node.getBoundingClientRect().width);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const ref = t2;
  let t3;
  if ($[3] !== width) {
    t3 = {
      ref,
      width
    };
    $[3] = width;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};

/**
 * Where the preferences rail stops paying for itself: 300px of preferences
 * beside 700 of days. Below it the rail becomes a button and a popover.
 */
export const RAIL_MIN_WIDTH = 1000;
export const hasRoomForRail = (width: number | null): boolean => width !== null && width >= RAIL_MIN_WIDTH;